import { round } from "../../../utils/math";
import { normalizeRole, RoleS } from "../../../utils/role-helpers";
import { QueueTypeS } from "../../../utils/queue-type-helpers";
import { CombatTierList, Champion, Role, RoleData } from "../../../interfaces/tier-lists/combat-tier-list.interface";

export interface CombatChampionInfo {
  role: RoleS;
  champion_id: Champion[0];
  champion_link: {
    champion_id: Champion[0];
    role: RoleS;
  };
  wins: Champion[1];
  matches: Champion[2];
  win_rate: number;
  pick_rate: number;
  avg_kda: number;
  kills: number;
  assists: number;
  deaths: number;
  damage: number;
  damage_taken: number;
  damage_mitigated: number;
  heals: number;
  cc_time_dealt: number;
  cc_time: number;
}

export function cleanCombat(data: CombatTierList) {
  const combat: Partial<Record<RoleS, CombatChampionInfo[]>> = {};
  let combatData = data[0];
  const last_updated = data[1];
  const total_matches = data[2];

  const entries: Array<[Role, RoleData]> = Object.entries(combatData) as any;
  for (const [role, roleData] of entries) {
    const normalizedRole = normalizeRole(role) as RoleS;
    const role_array: CombatChampionInfo[] = [];
    roleData.forEach((champInfo) => {
      const matches = champInfo[2];

      const cur_champ_info: CombatChampionInfo = {
        role: normalizedRole,
        champion_id: champInfo[0],
        champion_link: {
          champion_id: champInfo[0],
          role: normalizedRole,
        },
        wins: champInfo[1],
        matches,
        win_rate: round((champInfo[1] / matches) * 100, 2),
        pick_rate: round((matches / total_matches) * 100, 2),
        avg_kda: round((champInfo[3] + champInfo[4]) / champInfo[5], 2),
        kills: round(champInfo[3] / matches, 2),
        assists: round(champInfo[4] / matches, 2),
        deaths: round(champInfo[5] / matches, 2),
        damage: round(champInfo[6] / matches, 0),
        damage_taken: round(champInfo[7] / matches, 0),
        damage_mitigated: round(champInfo[8] / matches, 0),
        heals: round(champInfo[9] / matches, 0),
        cc_time_dealt: round(champInfo[10] / matches / 60, 2),
        cc_time: round(champInfo[11] / matches / 60, 2),
      };

      role_array.push(cur_champ_info);
    });

    const sorted_role_array = role_array.sort((a, b) => {
      // Sort by Win Rate
      return b.win_rate - a.win_rate;
    });

    combat[normalizedRole] = sorted_role_array;
  }

  const roleArray = Object.values(combat);
  const all_roles_array: CombatChampionInfo[] = roleArray.reduce((acc, curr) => {
    return [...acc, ...curr];
  }, []);
  const sorted_roles_array = all_roles_array.sort((a, b) => {
    // Sort by Win Rate
    return b.win_rate - a.win_rate;
  });

  combat[RoleS.ALL] = sorted_roles_array;

  return {
    combat,
    last_updated,
    total_matches,
  };
}

export function selectCombatData(combatData: ReturnType<typeof cleanCombat>, params: { queueType: string; role: string }) {
  let combat, last_updated, total_matches;

  if (!combatData) {
    return { combat, last_updated, total_matches };
  }

  let { queueType, role } = params;

  if (queueType === QueueTypeS.ARAM || queueType == QueueTypeS.NEXUS_BLITZ) {
    role = RoleS.NONE;
  } else {
    role = normalizeRole(role, RoleS.ALL);
  }

  if (combatData) {
    combat = combatData.combat[role as RoleS];
    last_updated = combatData.last_updated;
    total_matches = combatData.total_matches;
  }

  return { combat, last_updated, total_matches };
}
